import { OrdersDB } from '@/db/repositories';
import { isProductionEnv, onlineStatus } from '@/utils';
import * as OrdersService from '@/service/OrdersService';

const REFRESH_INTERVAL = Number(import.meta.env.TOURPWA_ORDERS_CACHE_REFRESH_INTERVAL_MS) || 600000;

export class OrdersCacheRefreshHelper {
  private timerId?: number;
  private cacheUpdatePromise?: Promise<void>;

  start() {
    window.addEventListener('online', this.handleOnline);
    window.addEventListener('offline', this.handleOffline);

    return this.updateCache();
  }

  stop() {
    this.reset();
    window.removeEventListener('online', this.handleOnline);
    window.removeEventListener('offline', this.handleOffline);
  }

  private reset() {
    window.clearTimeout(this.timerId);
    this.timerId = undefined;
    this.cacheUpdatePromise = undefined;
  }

  private handleOnline = () => {
    void this.updateCache();
  };

  private handleOffline = () => {
    this.reset();
  };

  updateCache() {
    if (!onlineStatus.get()) return Promise.resolve();
    this.reset();

    const cacheUpdatePromise = this.fetchAndStoreOrders();
    this.cacheUpdatePromise = cacheUpdatePromise;
    void cacheUpdatePromise.then(() => {
      if (this.cacheUpdatePromise !== cacheUpdatePromise) return;

      this.cacheUpdatePromise = undefined;
      this.timerId = window.setTimeout(() => {
        void this.updateCache();
      }, REFRESH_INTERVAL);
    });

    return cacheUpdatePromise;
  }

  private async fetchAndStoreOrders() {
    try {
      const previousIds = (await OrdersDB.getOfflineCache())?.results.map((order) => order.orderMstrId) ?? [];
      const response = await OrdersService.searchOrders({ offline: true });
      const lastOrdersSyncDate = new Date().toISOString();
      await OrdersDB.replaceOfflineCache(response, lastOrdersSyncDate, previousIds);

      if (!isProductionEnv) {
        console.info('Offline Orders cache updated.', {
          lastOrdersSyncDate,
          orderCount: response.results.length,
          searchRowCount: response.searchRowCount,
          readyToShipOrderCount: response.readyToShipOrderCount,
        });
      }
    } catch (error) {
      console.error('Failed to refresh the offline Orders cache:', error);
    }
  }
}

export const ordersCacheRefreshHelper = new OrdersCacheRefreshHelper();
